import React, { useRef, useState } from "react";

import SimpleBar from "@/ui/simplebar";
import {
    PiFile,
    PiFileCsv,
    PiFileDoc,
    PiFilePdf,
    PiFileXls,
    PiFileZip,
    PiTrashBold,
} from "react-icons/pi";
import { ActionIcon, FileInput, FileInputProps, Text } from "rizzui";

type AcceptedFiles = "img" | "pdf" | "csv" | "imgAndPdf" | "all";

const fileType = {
    "text/csv": <PiFileCsv className="h-5 w-5" />,
    "text/plain": <PiFile className="h-5 w-5" />,
    "application/pdf": <PiFilePdf className="h-5 w-5" />,
    "application/xml": <PiFileXls className="h-5 w-5" />,
    "application/zip": <PiFileZip className="h-5 w-5" />,
    "application/gzip": <PiFileZip className="h-5 w-5" />,
    "application/msword": <PiFileDoc className="h-5 w-5" />,
} as { [key: string]: React.ReactElement };

export default function FileUploadField({
    label,
    multiple = true,
    accept = "img",
    className,
    name,
    register,
    onChange,
    ...props
}: FileInputProps & {
    className?: string;
    name: string;
    label?: React.ReactNode;
    multiple?: boolean;
    register?;
    accept?: AcceptedFiles;
    onChange?: (event: React.ChangeEvent) => void;
}) {
    const [files, setFiles] = useState<Array<File>>([]);
    const imageRef = useRef<HTMLInputElement>(null);

    function handleFileDrop(event: React.ChangeEvent<HTMLInputElement>) {
        const uploadedFiles = event.target.files;
        const newFiles = Object.entries(uploadedFiles as object)
            .map((file) => {
                if (file[1]) return file[1];
            })
            .filter((file) => file !== undefined);

        setFiles((prevFiles) => [...prevFiles, ...newFiles]);

        onChange?.(event);
    }

    function handleImageDelete(index: number) {
        const updatedFiles = files.filter((_, i) => i !== index);
        setFiles(updatedFiles);
        (imageRef.current as HTMLInputElement).value = "";
    }

    return (
        <div className={className}>
            {((!multiple && files?.length < 1) || multiple) && (
                <FileInput
                    label={label}
                    ref={imageRef}
                    // accept={accept}
                    name={name}
                    multiple={multiple}
                    onChange={(event) => handleFileDrop(event)}
                    className="w-full"
                    inputClassName="p-0 ps-px"
                    {...(register && register)}
                    {...props}
                />
            )}

            {files.length > 1 ? (
                <Text className="mb-2 text-gray-500">{files.length} files</Text>
            ) : null}

            {files.length > 0 && (
                <SimpleBar className="mt-6">
                    <div className="grid grid-cols-1 gap-4">
                        {files?.map((file: File, index: number) => (
                            <div
                                className="flex min-h-[58px] w-full items-center rounded-xl border border-muted px-3 dark:border-gray-300"
                                key={file.name}
                            >
                                <div className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-muted bg-gray-50 object-cover px-2 py-1.5 dark:bg-transparent">
                                    {file.type.includes("image") ? (
                                        <img
                                            src={URL.createObjectURL(file)}
                                            className="absolute inset-0 w-full h-full object-cover"
                                            alt={file.name}
                                            sizes="(max-width: 768px) 100vw"
                                        />
                                    ) : (
                                        <>{fileType[file.type]}</>
                                    )}
                                </div>
                                <div className="truncate px-2.5">
                                    {file.name}
                                </div>
                                <ActionIcon
                                    onClick={() => handleImageDelete(index)}
                                    size="sm"
                                    variant="flat"
                                    color="danger"
                                    className="ms-auto flex-shrink-0 p-0 dark:bg-red-dark/20"
                                >
                                    <PiTrashBold className="w-6" />
                                </ActionIcon>
                            </div>
                        ))}
                    </div>
                    {props?.error && (
                        <Text className="mt-2 text-red-500">{props.error}</Text>
                    )}
                </SimpleBar>
            )}
        </div>
    );
}
